import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { BillingFrequency, BillingStrategy } from '@pncl/common-models';
import { HttpClient } from '@angular/common/http';
import { map, shareReplay } from 'rxjs';
import {
  PaymentPlan,
  PaymentPlanFeature,
  FeatureName,
  Feature,
  PlanFeatureSet,
  FeatureType,
} from '../models/payment';
import { URLService } from './dynamic-url.service';

@Injectable({
  providedIn: 'root',
})
export class PlanDetailsService {
  public plans$: Observable<PaymentPlan[]>;

  constructor(
    private translateService: TranslateService,
    private http: HttpClient,
    private urlService: URLService,
  ) {
    this.plans$ = this.fetchPlans().pipe(shareReplay(1));
  }

  private featureCategories = [
    {
      key: FeatureType.PRICING,
      value: 'Pricing',
    },
    {
      key: FeatureType.LIVE_TEACHING,
      value: 'Live teaching',
    },
    {
      key: FeatureType.CALLING,
      value: 'Calling',
    },
    {
      key: FeatureType.SCHEDULING,
      value: 'Scheduling',
    },
    {
      key: FeatureType.RECORDING,
      value: 'Recording',
    },
    {
      key: FeatureType.APPS,
      value: 'Apps',
    },
    {
      key: FeatureType.AI_AND_ALERTS,
      value: 'AI and Alerts',
    },
    {
      key: FeatureType.ANALYTICS,
      value: 'Analytics',
    },
    {
      key: FeatureType.INTEGRATIONS,
      value: 'Integrations',
    },
    {
      key: FeatureType.TRAINING_ONBOARDING_SUPPORT,
      value: 'Training, Onboarding, and Support',
    },
    {
      key: FeatureType.ENTERPRISE,
      value: 'Enterprise',
    },
  ];

  // Complete list of all features shown in the comparison table
  private features: Feature[] = [
    // Pricing
    {
      name: FeatureName.FIRST_4_USERS,
      value: this.translateService.instant('First 4 users'),
      type: FeatureType.PRICING,
    },
    {
      name: FeatureName.EACH_USER_THEREAFTER,
      value: this.translateService.instant('And for each user thereafter'),
      type: FeatureType.PRICING,
    },
    {
      name: FeatureName.MONTHLY_MINIMUM,
      value: this.translateService.instant('Monthly minimum'),
      type: FeatureType.PRICING,
    },

    // Live Teaching
    {
      name: FeatureName.NUMBER_OF_SPACES,
      value: this.translateService.instant('# of Spaces'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.NUMBER_OF_PEOPLE_IN_SPACE,
      value: this.translateService.instant('# of people in Space'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.NUMBER_OF_BOARDS_IN_SPACE,
      value: this.translateService.instant('# of boards in Space'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.PERSISTENT_SPACE_LINKS,
      value: this.translateService.instant('Persistent Space links'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.UPLOAD_PDFS_IMAGES,
      value: this.translateService.instant('Upload PDFs/images'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.RESOURCE_LIBRARY,
      value: this.translateService.instant('Resource library (10,000+ resources)'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.LEAD_LOCK_AND_FOLLOW,
      value: this.translateService.instant('Lead, Lock and Follow'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.DISTRACTION_ALERTS,
      value: this.translateService.instant('Distraction alerts'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.CHATS,
      value: this.translateService.instant('Chats (Private and Space-level)'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.CELEBRATIONS_AND_REACTIONS,
      value: this.translateService.instant('Celebrations and Reactions'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.PARTICIPANT_MANAGEMENT,
      value: this.translateService.instant('Participant management'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.WAITING_ROOM,
      value: this.translateService.instant('Waiting room'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.RAISE_HANDS,
      value: this.translateService.instant('Raise Hands'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.EXPORT_SPACES_AS_IMAGE,
      value: this.translateService.instant('Export Spaces as image'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.EXPORT_SPACES_AS_SPACE_FILE,
      value: this.translateService.instant('Export Spaces as .space file'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.BREAKOUT_ROOMS,
      value: this.translateService.instant('Breakout rooms'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.OBSERVER_MODE,
      value: this.translateService.instant('Observer mode'),
      type: FeatureType.LIVE_TEACHING,
    },
    {
      name: FeatureName.CHILD_MODE,
      value: this.translateService.instant('Child mode'),
      type: FeatureType.LIVE_TEACHING,
    },

    // Calling
    {
      name: FeatureName.VIDEO_CALLING,
      value: this.translateService.instant('Video calling'),
      type: FeatureType.CALLING,
    },
    {
      name: FeatureName.ADVANCED_NOISE_CANCELLATION,
      value: this.translateService.instant('Advanced noise cancellation'),
      type: FeatureType.CALLING,
    },
    {
      name: FeatureName.ANNOTATE_OVER_SCREENSHARE,
      value: this.translateService.instant('Annotate over screenshare'),
      type: FeatureType.CALLING,
    },
    {
      name: FeatureName.VIRTUAL_BACKGROUNDS,
      value: this.translateService.instant('Virtual backgrounds'),
      type: FeatureType.CALLING,
    },
    {
      name: FeatureName.FOCUS_MODE,
      value: this.translateService.instant('Focus mode (Coming Soon)'),
      type: FeatureType.CALLING,
    },
    {
      name: FeatureName.ENFORCE_VIRTUAL_BACKGROUNDS,
      value: this.translateService.instant('Enforce virtual backgrounds'),
      type: FeatureType.CALLING,
    },
    {
      name: FeatureName.PHONE_DIAL_IN,
      value: this.translateService.instant('Phone dial-in (Coming Soon)'),
      type: FeatureType.CALLING,
    },
    {
      name: FeatureName.LIVESTREAMING,
      value: this.translateService.instant('Livestreaming (Coming Soon)'),
      type: FeatureType.CALLING,
    },

    // Scheduling
    {
      name: FeatureName.CREATE_SCHEDULED_EVENTS,
      value: this.translateService.instant('Create scheduled events'),
      type: FeatureType.SCHEDULING,
    },
    {
      name: FeatureName.AVAILABILITY,
      value: this.translateService.instant('Availability'),
      type: FeatureType.SCHEDULING,
    },
    {
      name: FeatureName.REMINDERS,
      value: this.translateService.instant('Reminders'),
      type: FeatureType.SCHEDULING,
    },
    {
      name: FeatureName.BULK_SCHEDULING,
      value: this.translateService.instant('Bulk Scheduling'),
      type: FeatureType.SCHEDULING,
    },

    // Recording
    {
      name: FeatureName.LOCAL_RECORDING,
      value: this.translateService.instant('Local recording'),
      type: FeatureType.RECORDING,
    },
    {
      name: FeatureName.CLOUD_RECORDING,
      value: this.translateService.instant('Cloud recording'),
      type: FeatureType.RECORDING,
    },
    {
      name: FeatureName.HIDE_STUDENT_VIDEO_FROM_RECORDING,
      value: this.translateService.instant('Hide student video from recording'),
      type: FeatureType.RECORDING,
    },
    {
      name: FeatureName.DOWNLOADABLE_CLOUD_RECORDINGS,
      value: this.translateService.instant('Downloadable cloud recordings'),
      type: FeatureType.RECORDING,
    },
    {
      name: FeatureName.RECORDING_OF_BREAKOUT_ROOMS,
      value: this.translateService.instant('Recording of breakout rooms'),
      type: FeatureType.RECORDING,
    },
    {
      name: FeatureName.EXPORT_TO_S3_BUCKET,
      value: this.translateService.instant('Export to S3 bucket'),
      type: FeatureType.RECORDING,
    },
    {
      name: FeatureName.CLOUD_RECORDING_STORAGE_MONTHS,
      value: this.translateService.instant('Cloud recording storage (months)'),
      type: FeatureType.RECORDING,
    },

    // Apps
    {
      name: FeatureName.IMPORT_FROM_ONEDRIVE_GOOGLE_DRIVE,
      value: this.translateService.instant('Import from OneDrive / Google Drive'),
      type: FeatureType.APPS,
    },
    {
      name: FeatureName.IMPORT_FROM_PENCIL_FILES,
      value: this.translateService.instant('Import from Pencil Files (Coming Soon)'),
      type: FeatureType.APPS,
    },
    {
      name: FeatureName.POLLS_TIMER_STOPWATCH,
      value: this.translateService.instant('Polls, Timer, Stopwatch'),
      type: FeatureType.APPS,
    },
    {
      name: FeatureName.GOOGLE_DOCS_SHEETS_SLIDES,
      value: this.translateService.instant('Google Docs, Sheets, and Slides'),
      type: FeatureType.APPS,
    },
    {
      name: FeatureName.KAHOOT_NEARPOD_WAYGROUND_QUIZLET_IFRAME,
      value: this.translateService.instant(
        'Kahoot, Nearpod, Wayground, Quizlet, and other iFrame apps',
      ),
      type: FeatureType.APPS,
    },
    {
      name: FeatureName.COBROWSER,
      value: this.translateService.instant('Cobrowser'),
      type: FeatureType.APPS,
    },
    {
      name: FeatureName.COLLABORATIVE_BUILT_IN_APPS,
      value: this.translateService.instant('Collaborative built-in apps'),
      type: FeatureType.APPS,
    },

    // AI and Alerts
    {
      name: FeatureName.SESSION_TRANSCRIPTS,
      value: this.translateService.instant('Session transcripts'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.SESSION_SUMMARIES,
      value: this.translateService.instant('Session summaries'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.LESSON_GENERATOR,
      value: this.translateService.instant('Lesson Generator'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.AUTOGRADER,
      value: this.translateService.instant('Autograder'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.CUSTOM_SUMMARY_TEMPLATES,
      value: this.translateService.instant('Custom summary templates (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.POST_SESSION_COACH,
      value: this.translateService.instant('Post-session Coach'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.POST_SESSION_SAFEGUARDING_REPORT,
      value: this.translateService.instant('Post-session safeguarding report (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.POST_SESSION_DISINTERMEDIATION_ALERT,
      value: this.translateService.instant('Post-session disintermediation alert (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.POST_SESSION_STUDENT_REPORT,
      value: this.translateService.instant('Post-session student report (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.REAL_TIME_SAFEGUARDING_ALERTS,
      value: this.translateService.instant('Real-time safeguarding alerts (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.REAL_TIME_COACH,
      value: this.translateService.instant('Real-time coach (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.CLOSED_CAPTIONS,
      value: this.translateService.instant('Closed captions (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },
    {
      name: FeatureName.ADDON_REAL_TIME_AUDIO_TRANSLATION,
      value: this.translateService.instant('Add-on: Real-time audio translation (Coming Soon)'),
      type: FeatureType.AI_AND_ALERTS,
    },

    // Analytics
    {
      name: FeatureName.SESSIONS_HISTORY_VIEW,
      value: this.translateService.instant('Sessions history view (Coming Soon)'),
      type: FeatureType.ANALYTICS,
    },
    {
      name: FeatureName.SESSION_FEEDBACK,
      value: this.translateService.instant('Session feedback (Coming Soon)'),
      type: FeatureType.ANALYTICS,
    },
    {
      name: FeatureName.PER_SESSION_ENGAGEMENT_ANALYTICS,
      value: this.translateService.instant('Per-session engagement analytics'),
      type: FeatureType.ANALYTICS,
    },
    {
      name: FeatureName.ALERT_CENTER,
      value: this.translateService.instant('Alert center'),
      type: FeatureType.ANALYTICS,
    },
    {
      name: FeatureName.TEACHER_STUDENT_DASHBOARDS,
      value: this.translateService.instant('Teacher and student dashboards (Coming Soon)'),
      type: FeatureType.ANALYTICS,
    },

    // Integrations
    {
      name: FeatureName.NATIVE_APPS,
      value: this.translateService.instant('Native apps (iOS, Android, Desktop)'),
      type: FeatureType.INTEGRATIONS,
    },
    {
      name: FeatureName.TMS_LMS_INTEGRATIONS,
      value: this.translateService.instant('TMS / LMS integrations (Teachworks, TutorCruncher)'),
      type: FeatureType.INTEGRATIONS,
    },
    {
      name: FeatureName.API_AND_WEBHOOKS,
      value: this.translateService.instant('API and webhooks'),
      type: FeatureType.INTEGRATIONS,
    },
    {
      name: FeatureName.CHATGPT_CLAUDE_INTEGRATION,
      value: this.translateService.instant('ChatGPT / Claude integration'),
      type: FeatureType.INTEGRATIONS,
    },

    // Training, Onboarding, and Support
    {
      name: FeatureName.LOCALIZATION_AND_RTL,
      value: this.translateService.instant('Localization and RTL support'),
      type: FeatureType.TRAINING_ONBOARDING_SUPPORT,
    },
    {
      name: FeatureName.SUPPORT_24_7,
      value: this.translateService.instant('24/7 Live Support'),
      type: FeatureType.TRAINING_ONBOARDING_SUPPORT,
    },
    {
      name: FeatureName.SELF_SERVE_TRAINING,
      value: this.translateService.instant('Self-serve training for teachers'),
      type: FeatureType.TRAINING_ONBOARDING_SUPPORT,
    },
    {
      name: FeatureName.DEDICATED_ADMIN_ONBOARDING,
      value: this.translateService.instant('Dedicated admin onboarding'),
      type: FeatureType.TRAINING_ONBOARDING_SUPPORT,
    },
    {
      name: FeatureName.DEDICATED_ACCOUNT_MANAGER,
      value: this.translateService.instant('Dedicated account manager'),
      type: FeatureType.TRAINING_ONBOARDING_SUPPORT,
    },

    // Enterprise
    {
      name: FeatureName.COMPLIANCE,
      value: this.translateService.instant(
        'Compliance: WCAG 2.1, GDPR, COPPA, FERPA, HIPPAA, SOC II Type 2',
      ),
      type: FeatureType.ENTERPRISE,
    },
    {
      name: FeatureName.INSTITUTION_TEMPLATES,
      value: this.translateService.instant('Institution templates'),
      type: FeatureType.ENTERPRISE,
    },
    {
      name: FeatureName.BRANDING,
      value: this.translateService.instant('Branding'),
      type: FeatureType.ENTERPRISE,
    },
    {
      name: FeatureName.MULTI_SITE_SUPPORT,
      value: this.translateService.instant('Multi-site support'),
      type: FeatureType.ENTERPRISE,
    },
    {
      name: FeatureName.VOLUME_BASED_DISCOUNTS,
      value: this.translateService.instant('Volume based discounts'),
      type: FeatureType.ENTERPRISE,
    },
    {
      name: FeatureName.BULK_PRE_PURCHASE_HOURS,
      value: this.translateService.instant('Bulk pre-purchase hours'),
      type: FeatureType.ENTERPRISE,
    },
    {
      name: FeatureName.ADDON_CUSTOM_DEVELOPMENT,
      value: this.translateService.instant('Add-on: Custom development'),
      type: FeatureType.ENTERPRISE,
    },
    {
      name: FeatureName.ADDON_SIERRA_TMS,
      value: this.translateService.instant('Add-on: Sierra TMS'),
      type: FeatureType.ENTERPRISE,
    },
  ];

  // Shared utility function to group features by category
  private groupFeaturesByCategory<T extends { type: FeatureType }>(
    features: T[],
  ): Map<FeatureType, T[]> {
    const featuresByCategory = new Map<FeatureType, T[]>();

    this.featureCategories.forEach((category) => {
      featuresByCategory.set(category.key, []);
    });

    features.forEach((feature) => {
      const categoryFeatures = featuresByCategory.get(feature.type);
      if (categoryFeatures) {
        categoryFeatures.push(feature);
      }
    });

    return featuresByCategory;
  }

  getFeatures(): Feature[] {
    return [...this.features];
  }

  getFeaturesByCategory(): Map<FeatureType, Feature[]> {
    return this.groupFeaturesByCategory(this.features);
  }

  getFeatureCategories(): { key: FeatureType; value: string }[] {
    return this.featureCategories;
  }

  fetchPlans(): Observable<PaymentPlan[]> {
    return this.http
      .get<{ plans: PaymentPlan[] }>(`${this.urlService.getDynamicUrl()}/billing/plans`)
      .pipe(map(({ plans }) => plans));
  }

  /**
   * Build the composite key used to look up a per-plan feature mapping.
   * Derived from the plan object itself — no hardcoded strategy/frequency/name checks.
   */
  private getPlanMappingKey(plan: {
    billingStrategy: BillingStrategy;
    billingFrequency: BillingFrequency | string;
    name: string;
  }): string {
    return `${plan.billingStrategy}:${plan.billingFrequency}:${plan.name}`;
  }

  private featureMappingCache = new Map<string, PlanFeatureSet | undefined>();

  /**
   * Get the feature mapping for a specific plan.
   * Looks up by billingStrategy + billingFrequency + plan name.
   */
  getFeatureMappingForPlan(plan: {
    billingStrategy: BillingStrategy;
    billingFrequency: BillingFrequency | string;
    name: string;
  }): PlanFeatureSet | undefined {
    const key = this.getPlanMappingKey(plan);
    if (this.featureMappingCache.has(key)) {
      return this.featureMappingCache.get(key);
    }

    const mapping = this.planFeatureMappings[key];
    if (!mapping) {
      this.featureMappingCache.set(key, undefined);
      return undefined;
    }

    const mappedFeatures = this.features.map((feature) => {
      const featureValue = mapping.features.find(
        (f: PaymentPlanFeature) => f.name === feature.name,
      );
      return {
        name: feature.name,
        value: featureValue?.value || '',
        enabled: featureValue?.isIncludedInPlan || false,
        type: feature.type,
      };
    });

    const result: PlanFeatureSet = {
      ...mapping,
      mappedFeaturesByCategory: this.groupFeaturesByCategory(mappedFeatures),
    };
    this.featureMappingCache.set(key, result);
    return result;
  }

  /**
   * Per-plan feature mappings keyed by `${billingStrategy}:${billingFrequency}:${planName}`.
   * Covers usage_based monthly plans (Pro, Expert, Master) and custom (Scale).
   */
  private planFeatureMappings: Record<string, Omit<PlanFeatureSet, 'mappedFeaturesByCategory'>> = {
    // ── Pro ──────────────────────────────────────────────────────────────
    [`${BillingStrategy.USAGE_BASED}:${BillingFrequency.Monthly}:Pro`]: {
      billingStrategy: BillingStrategy.USAGE_BASED,
      billingFrequency: BillingFrequency.Monthly,
      planName: 'Pro',
      features: [
        // Pricing
        { name: FeatureName.FIRST_4_USERS, value: '$0.40/hr', isIncludedInPlan: true },
        { name: FeatureName.EACH_USER_THEREAFTER, value: '$0.20/hr', isIncludedInPlan: true },
        { name: FeatureName.MONTHLY_MINIMUM, value: '$4', isIncludedInPlan: true },

        // Live Teaching
        { name: FeatureName.NUMBER_OF_SPACES, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_PEOPLE_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_BOARDS_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.PERSISTENT_SPACE_LINKS, isIncludedInPlan: true },
        { name: FeatureName.UPLOAD_PDFS_IMAGES, isIncludedInPlan: true },
        { name: FeatureName.RESOURCE_LIBRARY, isIncludedInPlan: true },
        { name: FeatureName.LEAD_LOCK_AND_FOLLOW, isIncludedInPlan: true },
        { name: FeatureName.DISTRACTION_ALERTS, isIncludedInPlan: true },
        { name: FeatureName.CHATS, isIncludedInPlan: true },
        { name: FeatureName.CELEBRATIONS_AND_REACTIONS, isIncludedInPlan: true },
        { name: FeatureName.PARTICIPANT_MANAGEMENT, isIncludedInPlan: true },
        { name: FeatureName.WAITING_ROOM, isIncludedInPlan: true },
        { name: FeatureName.RAISE_HANDS, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_IMAGE, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_SPACE_FILE, isIncludedInPlan: true },
        { name: FeatureName.BREAKOUT_ROOMS, isIncludedInPlan: true },
        { name: FeatureName.OBSERVER_MODE, isIncludedInPlan: true },
        { name: FeatureName.CHILD_MODE, isIncludedInPlan: false },

        // Calling
        { name: FeatureName.VIDEO_CALLING, isIncludedInPlan: true },
        { name: FeatureName.ADVANCED_NOISE_CANCELLATION, isIncludedInPlan: true },
        { name: FeatureName.ANNOTATE_OVER_SCREENSHARE, isIncludedInPlan: true },
        { name: FeatureName.VIRTUAL_BACKGROUNDS, isIncludedInPlan: true },
        { name: FeatureName.FOCUS_MODE, isIncludedInPlan: true },
        { name: FeatureName.ENFORCE_VIRTUAL_BACKGROUNDS, isIncludedInPlan: false },
        { name: FeatureName.PHONE_DIAL_IN, isIncludedInPlan: false },
        { name: FeatureName.LIVESTREAMING, isIncludedInPlan: false },

        // Scheduling
        { name: FeatureName.CREATE_SCHEDULED_EVENTS, isIncludedInPlan: true },
        { name: FeatureName.AVAILABILITY, isIncludedInPlan: true },
        { name: FeatureName.REMINDERS, value: 'Email only', isIncludedInPlan: true },
        { name: FeatureName.BULK_SCHEDULING, isIncludedInPlan: false },

        // Recording
        { name: FeatureName.LOCAL_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.CLOUD_RECORDING, isIncludedInPlan: false },
        { name: FeatureName.HIDE_STUDENT_VIDEO_FROM_RECORDING, isIncludedInPlan: false },
        { name: FeatureName.DOWNLOADABLE_CLOUD_RECORDINGS, isIncludedInPlan: false },
        { name: FeatureName.RECORDING_OF_BREAKOUT_ROOMS, isIncludedInPlan: false },
        { name: FeatureName.EXPORT_TO_S3_BUCKET, isIncludedInPlan: false },
        { name: FeatureName.CLOUD_RECORDING_STORAGE_MONTHS, value: '-', isIncludedInPlan: false },

        // Apps
        { name: FeatureName.IMPORT_FROM_ONEDRIVE_GOOGLE_DRIVE, isIncludedInPlan: false },
        { name: FeatureName.IMPORT_FROM_PENCIL_FILES, isIncludedInPlan: false },
        { name: FeatureName.POLLS_TIMER_STOPWATCH, isIncludedInPlan: false },
        { name: FeatureName.GOOGLE_DOCS_SHEETS_SLIDES, isIncludedInPlan: false },
        { name: FeatureName.KAHOOT_NEARPOD_WAYGROUND_QUIZLET_IFRAME, isIncludedInPlan: false },
        { name: FeatureName.COBROWSER, isIncludedInPlan: false },
        { name: FeatureName.COLLABORATIVE_BUILT_IN_APPS, isIncludedInPlan: false },

        // AI and Alerts
        { name: FeatureName.SESSION_TRANSCRIPTS, isIncludedInPlan: false },
        { name: FeatureName.SESSION_SUMMARIES, isIncludedInPlan: false },
        { name: FeatureName.LESSON_GENERATOR, isIncludedInPlan: false },
        { name: FeatureName.AUTOGRADER, isIncludedInPlan: false },
        { name: FeatureName.CUSTOM_SUMMARY_TEMPLATES, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_COACH, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_SAFEGUARDING_REPORT, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_DISINTERMEDIATION_ALERT, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_STUDENT_REPORT, isIncludedInPlan: false },
        { name: FeatureName.REAL_TIME_SAFEGUARDING_ALERTS, isIncludedInPlan: false },
        { name: FeatureName.REAL_TIME_COACH, isIncludedInPlan: false },
        { name: FeatureName.CLOSED_CAPTIONS, isIncludedInPlan: false },
        { name: FeatureName.ADDON_REAL_TIME_AUDIO_TRANSLATION, isIncludedInPlan: false },

        // Analytics
        { name: FeatureName.SESSIONS_HISTORY_VIEW, isIncludedInPlan: true },
        { name: FeatureName.SESSION_FEEDBACK, isIncludedInPlan: false },
        { name: FeatureName.PER_SESSION_ENGAGEMENT_ANALYTICS, isIncludedInPlan: false },
        { name: FeatureName.ALERT_CENTER, isIncludedInPlan: false },
        { name: FeatureName.TEACHER_STUDENT_DASHBOARDS, isIncludedInPlan: false },

        // Integrations
        { name: FeatureName.NATIVE_APPS, isIncludedInPlan: true },
        { name: FeatureName.TMS_LMS_INTEGRATIONS, isIncludedInPlan: true },
        { name: FeatureName.API_AND_WEBHOOKS, isIncludedInPlan: true },
        { name: FeatureName.CHATGPT_CLAUDE_INTEGRATION, isIncludedInPlan: true },

        // Training, Onboarding, and Support
        { name: FeatureName.LOCALIZATION_AND_RTL, isIncludedInPlan: true },
        { name: FeatureName.SUPPORT_24_7, value: 'Basic', isIncludedInPlan: true },
        { name: FeatureName.SELF_SERVE_TRAINING, isIncludedInPlan: true },
        { name: FeatureName.DEDICATED_ADMIN_ONBOARDING, isIncludedInPlan: false },
        { name: FeatureName.DEDICATED_ACCOUNT_MANAGER, isIncludedInPlan: false },

        // Enterprise
        { name: FeatureName.COMPLIANCE, isIncludedInPlan: true },
        { name: FeatureName.INSTITUTION_TEMPLATES, isIncludedInPlan: false },
        { name: FeatureName.BRANDING, isIncludedInPlan: false },
        { name: FeatureName.MULTI_SITE_SUPPORT, isIncludedInPlan: false },
        { name: FeatureName.VOLUME_BASED_DISCOUNTS, isIncludedInPlan: false },
        { name: FeatureName.BULK_PRE_PURCHASE_HOURS, isIncludedInPlan: false },
        { name: FeatureName.ADDON_CUSTOM_DEVELOPMENT, isIncludedInPlan: false },
        { name: FeatureName.ADDON_SIERRA_TMS, isIncludedInPlan: false },
      ],
    },

    // ── Expert ────────────────────────────────────────────────────────────
    [`${BillingStrategy.USAGE_BASED}:${BillingFrequency.Monthly}:Expert`]: {
      billingStrategy: BillingStrategy.USAGE_BASED,
      billingFrequency: BillingFrequency.Monthly,
      planName: 'Expert',
      features: [
        // Pricing
        { name: FeatureName.FIRST_4_USERS, value: '$1.00/hr', isIncludedInPlan: true },
        { name: FeatureName.EACH_USER_THEREAFTER, value: '$0.50/hr', isIncludedInPlan: true },
        { name: FeatureName.MONTHLY_MINIMUM, value: '$10', isIncludedInPlan: true },

        // Live Teaching
        { name: FeatureName.NUMBER_OF_SPACES, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_PEOPLE_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_BOARDS_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.PERSISTENT_SPACE_LINKS, isIncludedInPlan: true },
        { name: FeatureName.UPLOAD_PDFS_IMAGES, isIncludedInPlan: true },
        { name: FeatureName.RESOURCE_LIBRARY, isIncludedInPlan: true },
        { name: FeatureName.LEAD_LOCK_AND_FOLLOW, isIncludedInPlan: true },
        { name: FeatureName.DISTRACTION_ALERTS, isIncludedInPlan: true },
        { name: FeatureName.CHATS, isIncludedInPlan: true },
        { name: FeatureName.CELEBRATIONS_AND_REACTIONS, isIncludedInPlan: true },
        { name: FeatureName.PARTICIPANT_MANAGEMENT, isIncludedInPlan: true },
        { name: FeatureName.WAITING_ROOM, isIncludedInPlan: true },
        { name: FeatureName.RAISE_HANDS, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_IMAGE, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_SPACE_FILE, isIncludedInPlan: true },
        { name: FeatureName.BREAKOUT_ROOMS, isIncludedInPlan: true },
        { name: FeatureName.OBSERVER_MODE, isIncludedInPlan: true },
        { name: FeatureName.CHILD_MODE, isIncludedInPlan: true },

        // Calling
        { name: FeatureName.VIDEO_CALLING, isIncludedInPlan: true },
        { name: FeatureName.ADVANCED_NOISE_CANCELLATION, isIncludedInPlan: true },
        { name: FeatureName.ANNOTATE_OVER_SCREENSHARE, isIncludedInPlan: true },
        { name: FeatureName.VIRTUAL_BACKGROUNDS, isIncludedInPlan: true },
        { name: FeatureName.FOCUS_MODE, isIncludedInPlan: true },
        { name: FeatureName.ENFORCE_VIRTUAL_BACKGROUNDS, isIncludedInPlan: false },
        { name: FeatureName.PHONE_DIAL_IN, isIncludedInPlan: false },
        { name: FeatureName.LIVESTREAMING, isIncludedInPlan: false },

        // Scheduling
        { name: FeatureName.CREATE_SCHEDULED_EVENTS, isIncludedInPlan: true },
        { name: FeatureName.AVAILABILITY, isIncludedInPlan: true },
        { name: FeatureName.REMINDERS, value: 'Email only', isIncludedInPlan: true },
        { name: FeatureName.BULK_SCHEDULING, isIncludedInPlan: false },

        // Recording
        { name: FeatureName.LOCAL_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.CLOUD_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.HIDE_STUDENT_VIDEO_FROM_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.DOWNLOADABLE_CLOUD_RECORDINGS, isIncludedInPlan: false },
        { name: FeatureName.RECORDING_OF_BREAKOUT_ROOMS, isIncludedInPlan: false },
        { name: FeatureName.EXPORT_TO_S3_BUCKET, isIncludedInPlan: false },
        { name: FeatureName.CLOUD_RECORDING_STORAGE_MONTHS, value: '3', isIncludedInPlan: true },

        // Apps
        { name: FeatureName.IMPORT_FROM_ONEDRIVE_GOOGLE_DRIVE, isIncludedInPlan: true },
        { name: FeatureName.IMPORT_FROM_PENCIL_FILES, isIncludedInPlan: true },
        { name: FeatureName.POLLS_TIMER_STOPWATCH, isIncludedInPlan: true },
        { name: FeatureName.GOOGLE_DOCS_SHEETS_SLIDES, isIncludedInPlan: true },
        { name: FeatureName.KAHOOT_NEARPOD_WAYGROUND_QUIZLET_IFRAME, isIncludedInPlan: true },
        { name: FeatureName.COBROWSER, isIncludedInPlan: true },
        { name: FeatureName.COLLABORATIVE_BUILT_IN_APPS, isIncludedInPlan: true },

        // AI and Alerts
        { name: FeatureName.SESSION_TRANSCRIPTS, isIncludedInPlan: true },
        { name: FeatureName.SESSION_SUMMARIES, isIncludedInPlan: true },
        { name: FeatureName.LESSON_GENERATOR, isIncludedInPlan: false },
        { name: FeatureName.AUTOGRADER, isIncludedInPlan: false },
        { name: FeatureName.CUSTOM_SUMMARY_TEMPLATES, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_COACH, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_SAFEGUARDING_REPORT, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_DISINTERMEDIATION_ALERT, isIncludedInPlan: false },
        { name: FeatureName.POST_SESSION_STUDENT_REPORT, isIncludedInPlan: false },
        { name: FeatureName.REAL_TIME_SAFEGUARDING_ALERTS, isIncludedInPlan: false },
        { name: FeatureName.REAL_TIME_COACH, isIncludedInPlan: false },
        { name: FeatureName.CLOSED_CAPTIONS, isIncludedInPlan: false },
        { name: FeatureName.ADDON_REAL_TIME_AUDIO_TRANSLATION, isIncludedInPlan: false },

        // Analytics
        { name: FeatureName.SESSIONS_HISTORY_VIEW, isIncludedInPlan: true },
        { name: FeatureName.SESSION_FEEDBACK, isIncludedInPlan: true },
        { name: FeatureName.PER_SESSION_ENGAGEMENT_ANALYTICS, isIncludedInPlan: true },
        { name: FeatureName.ALERT_CENTER, isIncludedInPlan: false },
        { name: FeatureName.TEACHER_STUDENT_DASHBOARDS, isIncludedInPlan: false },

        // Integrations
        { name: FeatureName.NATIVE_APPS, isIncludedInPlan: true },
        { name: FeatureName.TMS_LMS_INTEGRATIONS, isIncludedInPlan: true },
        { name: FeatureName.API_AND_WEBHOOKS, isIncludedInPlan: true },
        { name: FeatureName.CHATGPT_CLAUDE_INTEGRATION, isIncludedInPlan: true },

        // Training, Onboarding, and Support
        { name: FeatureName.LOCALIZATION_AND_RTL, isIncludedInPlan: true },
        { name: FeatureName.SUPPORT_24_7, value: 'Priority', isIncludedInPlan: true },
        { name: FeatureName.SELF_SERVE_TRAINING, isIncludedInPlan: true },
        { name: FeatureName.DEDICATED_ADMIN_ONBOARDING, isIncludedInPlan: true },
        { name: FeatureName.DEDICATED_ACCOUNT_MANAGER, isIncludedInPlan: false },

        // Enterprise
        { name: FeatureName.COMPLIANCE, isIncludedInPlan: true },
        { name: FeatureName.INSTITUTION_TEMPLATES, isIncludedInPlan: true },
        { name: FeatureName.BRANDING, isIncludedInPlan: true },
        { name: FeatureName.MULTI_SITE_SUPPORT, isIncludedInPlan: false },
        { name: FeatureName.VOLUME_BASED_DISCOUNTS, isIncludedInPlan: false },
        { name: FeatureName.BULK_PRE_PURCHASE_HOURS, isIncludedInPlan: false },
        { name: FeatureName.ADDON_CUSTOM_DEVELOPMENT, isIncludedInPlan: false },
        { name: FeatureName.ADDON_SIERRA_TMS, isIncludedInPlan: false },
      ],
    },

    // ── Master ────────────────────────────────────────────────────────────
    [`${BillingStrategy.USAGE_BASED}:${BillingFrequency.Monthly}:Master`]: {
      billingStrategy: BillingStrategy.USAGE_BASED,
      billingFrequency: BillingFrequency.Monthly,
      planName: 'Master',
      features: [
        // Pricing
        { name: FeatureName.FIRST_4_USERS, value: '$5.00/hr', isIncludedInPlan: true },
        { name: FeatureName.EACH_USER_THEREAFTER, value: '$1.00/hr', isIncludedInPlan: true },
        { name: FeatureName.MONTHLY_MINIMUM, value: '$50', isIncludedInPlan: true },

        // Live Teaching
        { name: FeatureName.NUMBER_OF_SPACES, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_PEOPLE_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_BOARDS_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.PERSISTENT_SPACE_LINKS, isIncludedInPlan: true },
        { name: FeatureName.UPLOAD_PDFS_IMAGES, isIncludedInPlan: true },
        { name: FeatureName.RESOURCE_LIBRARY, isIncludedInPlan: true },
        { name: FeatureName.LEAD_LOCK_AND_FOLLOW, isIncludedInPlan: true },
        { name: FeatureName.DISTRACTION_ALERTS, isIncludedInPlan: true },
        { name: FeatureName.CHATS, isIncludedInPlan: true },
        { name: FeatureName.CELEBRATIONS_AND_REACTIONS, isIncludedInPlan: true },
        { name: FeatureName.PARTICIPANT_MANAGEMENT, isIncludedInPlan: true },
        { name: FeatureName.WAITING_ROOM, isIncludedInPlan: true },
        { name: FeatureName.RAISE_HANDS, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_IMAGE, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_SPACE_FILE, isIncludedInPlan: true },
        { name: FeatureName.BREAKOUT_ROOMS, isIncludedInPlan: true },
        { name: FeatureName.OBSERVER_MODE, isIncludedInPlan: true },
        { name: FeatureName.CHILD_MODE, isIncludedInPlan: true },

        // Calling
        { name: FeatureName.VIDEO_CALLING, isIncludedInPlan: true },
        { name: FeatureName.ADVANCED_NOISE_CANCELLATION, isIncludedInPlan: true },
        { name: FeatureName.ANNOTATE_OVER_SCREENSHARE, isIncludedInPlan: true },
        { name: FeatureName.VIRTUAL_BACKGROUNDS, isIncludedInPlan: true },
        { name: FeatureName.FOCUS_MODE, isIncludedInPlan: true },
        { name: FeatureName.ENFORCE_VIRTUAL_BACKGROUNDS, isIncludedInPlan: true },
        { name: FeatureName.PHONE_DIAL_IN, isIncludedInPlan: true },
        { name: FeatureName.LIVESTREAMING, isIncludedInPlan: false },

        // Scheduling
        { name: FeatureName.CREATE_SCHEDULED_EVENTS, isIncludedInPlan: true },
        { name: FeatureName.AVAILABILITY, isIncludedInPlan: true },
        { name: FeatureName.REMINDERS, value: 'Email + SMS', isIncludedInPlan: true },
        { name: FeatureName.BULK_SCHEDULING, isIncludedInPlan: false },

        // Recording
        { name: FeatureName.LOCAL_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.CLOUD_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.HIDE_STUDENT_VIDEO_FROM_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.DOWNLOADABLE_CLOUD_RECORDINGS, isIncludedInPlan: true },
        { name: FeatureName.RECORDING_OF_BREAKOUT_ROOMS, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_TO_S3_BUCKET, isIncludedInPlan: false },
        { name: FeatureName.CLOUD_RECORDING_STORAGE_MONTHS, value: '12', isIncludedInPlan: true },

        // Apps
        { name: FeatureName.IMPORT_FROM_ONEDRIVE_GOOGLE_DRIVE, isIncludedInPlan: true },
        { name: FeatureName.IMPORT_FROM_PENCIL_FILES, isIncludedInPlan: true },
        { name: FeatureName.POLLS_TIMER_STOPWATCH, isIncludedInPlan: true },
        { name: FeatureName.GOOGLE_DOCS_SHEETS_SLIDES, isIncludedInPlan: true },
        { name: FeatureName.KAHOOT_NEARPOD_WAYGROUND_QUIZLET_IFRAME, isIncludedInPlan: true },
        { name: FeatureName.COBROWSER, isIncludedInPlan: true },
        { name: FeatureName.COLLABORATIVE_BUILT_IN_APPS, isIncludedInPlan: true },

        // AI and Alerts
        { name: FeatureName.SESSION_TRANSCRIPTS, isIncludedInPlan: true },
        { name: FeatureName.SESSION_SUMMARIES, isIncludedInPlan: true },
        { name: FeatureName.LESSON_GENERATOR, isIncludedInPlan: true },
        { name: FeatureName.AUTOGRADER, isIncludedInPlan: true },
        { name: FeatureName.CUSTOM_SUMMARY_TEMPLATES, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_COACH, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_SAFEGUARDING_REPORT, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_DISINTERMEDIATION_ALERT, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_STUDENT_REPORT, isIncludedInPlan: true },
        { name: FeatureName.REAL_TIME_SAFEGUARDING_ALERTS, isIncludedInPlan: true },
        { name: FeatureName.REAL_TIME_COACH, isIncludedInPlan: true },
        { name: FeatureName.CLOSED_CAPTIONS, isIncludedInPlan: true },
        { name: FeatureName.ADDON_REAL_TIME_AUDIO_TRANSLATION, isIncludedInPlan: false },

        // Analytics
        { name: FeatureName.SESSIONS_HISTORY_VIEW, isIncludedInPlan: true },
        { name: FeatureName.SESSION_FEEDBACK, isIncludedInPlan: true },
        { name: FeatureName.PER_SESSION_ENGAGEMENT_ANALYTICS, isIncludedInPlan: true },
        { name: FeatureName.ALERT_CENTER, isIncludedInPlan: true },
        { name: FeatureName.TEACHER_STUDENT_DASHBOARDS, isIncludedInPlan: true },

        // Integrations
        { name: FeatureName.NATIVE_APPS, isIncludedInPlan: true },
        { name: FeatureName.TMS_LMS_INTEGRATIONS, isIncludedInPlan: true },
        { name: FeatureName.API_AND_WEBHOOKS, isIncludedInPlan: true },
        { name: FeatureName.CHATGPT_CLAUDE_INTEGRATION, isIncludedInPlan: true },

        // Training, Onboarding, and Support
        { name: FeatureName.LOCALIZATION_AND_RTL, isIncludedInPlan: true },
        { name: FeatureName.SUPPORT_24_7, value: 'Priority', isIncludedInPlan: true },
        { name: FeatureName.SELF_SERVE_TRAINING, isIncludedInPlan: true },
        { name: FeatureName.DEDICATED_ADMIN_ONBOARDING, isIncludedInPlan: true },
        { name: FeatureName.DEDICATED_ACCOUNT_MANAGER, isIncludedInPlan: false },

        // Enterprise
        { name: FeatureName.COMPLIANCE, isIncludedInPlan: true },
        { name: FeatureName.INSTITUTION_TEMPLATES, isIncludedInPlan: true },
        { name: FeatureName.BRANDING, isIncludedInPlan: true },
        { name: FeatureName.MULTI_SITE_SUPPORT, isIncludedInPlan: false },
        { name: FeatureName.VOLUME_BASED_DISCOUNTS, isIncludedInPlan: false },
        { name: FeatureName.BULK_PRE_PURCHASE_HOURS, isIncludedInPlan: false },
        { name: FeatureName.ADDON_CUSTOM_DEVELOPMENT, isIncludedInPlan: false },
        { name: FeatureName.ADDON_SIERRA_TMS, isIncludedInPlan: false },
      ],
    },

    // ── Scale (Custom) ───────────────────────────────────────────────────
    [`${BillingStrategy.CUSTOM_BASED}:${BillingFrequency.Custom}:Scale`]: {
      billingStrategy: BillingStrategy.CUSTOM_BASED,
      billingFrequency: BillingFrequency.Custom,
      planName: 'Scale',
      features: [
        // Pricing
        { name: FeatureName.FIRST_4_USERS, value: 'Custom', isIncludedInPlan: true },
        { name: FeatureName.EACH_USER_THEREAFTER, value: 'Custom', isIncludedInPlan: true },
        { name: FeatureName.MONTHLY_MINIMUM, value: '$2,000', isIncludedInPlan: true },

        // Live Teaching
        { name: FeatureName.NUMBER_OF_SPACES, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_PEOPLE_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.NUMBER_OF_BOARDS_IN_SPACE, value: 'Unlimited', isIncludedInPlan: true },
        { name: FeatureName.PERSISTENT_SPACE_LINKS, isIncludedInPlan: true },
        { name: FeatureName.UPLOAD_PDFS_IMAGES, isIncludedInPlan: true },
        { name: FeatureName.RESOURCE_LIBRARY, isIncludedInPlan: true },
        { name: FeatureName.LEAD_LOCK_AND_FOLLOW, isIncludedInPlan: true },
        { name: FeatureName.DISTRACTION_ALERTS, isIncludedInPlan: true },
        { name: FeatureName.CHATS, isIncludedInPlan: true },
        { name: FeatureName.CELEBRATIONS_AND_REACTIONS, isIncludedInPlan: true },
        { name: FeatureName.PARTICIPANT_MANAGEMENT, isIncludedInPlan: true },
        { name: FeatureName.WAITING_ROOM, isIncludedInPlan: true },
        { name: FeatureName.RAISE_HANDS, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_IMAGE, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_SPACES_AS_SPACE_FILE, isIncludedInPlan: true },
        { name: FeatureName.BREAKOUT_ROOMS, isIncludedInPlan: true },
        { name: FeatureName.OBSERVER_MODE, isIncludedInPlan: true },
        { name: FeatureName.CHILD_MODE, isIncludedInPlan: true },

        // Calling
        { name: FeatureName.VIDEO_CALLING, isIncludedInPlan: true },
        { name: FeatureName.ADVANCED_NOISE_CANCELLATION, isIncludedInPlan: true },
        { name: FeatureName.ANNOTATE_OVER_SCREENSHARE, isIncludedInPlan: true },
        { name: FeatureName.VIRTUAL_BACKGROUNDS, isIncludedInPlan: true },
        { name: FeatureName.FOCUS_MODE, isIncludedInPlan: true },
        { name: FeatureName.ENFORCE_VIRTUAL_BACKGROUNDS, isIncludedInPlan: true },
        { name: FeatureName.PHONE_DIAL_IN, isIncludedInPlan: true },
        { name: FeatureName.LIVESTREAMING, isIncludedInPlan: true },

        // Scheduling
        { name: FeatureName.CREATE_SCHEDULED_EVENTS, isIncludedInPlan: true },
        { name: FeatureName.AVAILABILITY, isIncludedInPlan: true },
        { name: FeatureName.REMINDERS, value: 'Email + SMS', isIncludedInPlan: true },
        { name: FeatureName.BULK_SCHEDULING, isIncludedInPlan: true },

        // Recording
        { name: FeatureName.LOCAL_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.CLOUD_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.HIDE_STUDENT_VIDEO_FROM_RECORDING, isIncludedInPlan: true },
        { name: FeatureName.DOWNLOADABLE_CLOUD_RECORDINGS, isIncludedInPlan: true },
        { name: FeatureName.RECORDING_OF_BREAKOUT_ROOMS, isIncludedInPlan: true },
        { name: FeatureName.EXPORT_TO_S3_BUCKET, isIncludedInPlan: true },
        { name: FeatureName.CLOUD_RECORDING_STORAGE_MONTHS, value: '12+', isIncludedInPlan: true },

        // Apps
        { name: FeatureName.IMPORT_FROM_ONEDRIVE_GOOGLE_DRIVE, isIncludedInPlan: true },
        { name: FeatureName.IMPORT_FROM_PENCIL_FILES, isIncludedInPlan: true },
        { name: FeatureName.POLLS_TIMER_STOPWATCH, isIncludedInPlan: true },
        { name: FeatureName.GOOGLE_DOCS_SHEETS_SLIDES, isIncludedInPlan: true },
        { name: FeatureName.KAHOOT_NEARPOD_WAYGROUND_QUIZLET_IFRAME, isIncludedInPlan: true },
        { name: FeatureName.COBROWSER, isIncludedInPlan: true },
        { name: FeatureName.COLLABORATIVE_BUILT_IN_APPS, isIncludedInPlan: true },

        // AI and Alerts
        { name: FeatureName.SESSION_TRANSCRIPTS, isIncludedInPlan: true },
        { name: FeatureName.SESSION_SUMMARIES, isIncludedInPlan: true },
        { name: FeatureName.LESSON_GENERATOR, isIncludedInPlan: true },
        { name: FeatureName.AUTOGRADER, isIncludedInPlan: true },
        { name: FeatureName.CUSTOM_SUMMARY_TEMPLATES, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_COACH, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_SAFEGUARDING_REPORT, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_DISINTERMEDIATION_ALERT, isIncludedInPlan: true },
        { name: FeatureName.POST_SESSION_STUDENT_REPORT, isIncludedInPlan: true },
        { name: FeatureName.REAL_TIME_SAFEGUARDING_ALERTS, isIncludedInPlan: true },
        { name: FeatureName.REAL_TIME_COACH, isIncludedInPlan: true },
        { name: FeatureName.CLOSED_CAPTIONS, isIncludedInPlan: true },
        { name: FeatureName.ADDON_REAL_TIME_AUDIO_TRANSLATION, isIncludedInPlan: true },

        // Analytics
        { name: FeatureName.SESSIONS_HISTORY_VIEW, isIncludedInPlan: true },
        { name: FeatureName.SESSION_FEEDBACK, isIncludedInPlan: true },
        { name: FeatureName.PER_SESSION_ENGAGEMENT_ANALYTICS, isIncludedInPlan: true },
        { name: FeatureName.ALERT_CENTER, isIncludedInPlan: true },
        { name: FeatureName.TEACHER_STUDENT_DASHBOARDS, isIncludedInPlan: true },

        // Integrations
        { name: FeatureName.NATIVE_APPS, isIncludedInPlan: true },
        { name: FeatureName.TMS_LMS_INTEGRATIONS, isIncludedInPlan: true },
        { name: FeatureName.API_AND_WEBHOOKS, isIncludedInPlan: true },
        { name: FeatureName.CHATGPT_CLAUDE_INTEGRATION, isIncludedInPlan: true },

        // Training, Onboarding, and Support
        { name: FeatureName.LOCALIZATION_AND_RTL, isIncludedInPlan: true },
        { name: FeatureName.SUPPORT_24_7, value: 'Priority', isIncludedInPlan: true },
        { name: FeatureName.SELF_SERVE_TRAINING, isIncludedInPlan: true },
        { name: FeatureName.DEDICATED_ADMIN_ONBOARDING, isIncludedInPlan: true },
        { name: FeatureName.DEDICATED_ACCOUNT_MANAGER, isIncludedInPlan: true },

        // Enterprise
        { name: FeatureName.COMPLIANCE, isIncludedInPlan: true },
        { name: FeatureName.INSTITUTION_TEMPLATES, isIncludedInPlan: true },
        { name: FeatureName.BRANDING, isIncludedInPlan: true },
        { name: FeatureName.MULTI_SITE_SUPPORT, isIncludedInPlan: true },
        { name: FeatureName.VOLUME_BASED_DISCOUNTS, isIncludedInPlan: true },
        { name: FeatureName.BULK_PRE_PURCHASE_HOURS, isIncludedInPlan: true },
        { name: FeatureName.ADDON_CUSTOM_DEVELOPMENT, isIncludedInPlan: true },
        { name: FeatureName.ADDON_SIERRA_TMS, isIncludedInPlan: true },
      ],
    },
  };

  // Get a specific plan by planId
  getPlanByPlanId(planId?: string): Observable<PaymentPlan | undefined> {
    return this.plans$.pipe(map((plans) => plans.find((plan) => plan.planId === planId)));
  }
}
